// Default image data URI (SVG fallback for better visual feedback)
export const DEFAULT_IMAGE_DATA_URI = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZWVlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTk5OTkiPkltYWdlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+";

const API_HOST = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
const S3_IMAGE_PROXY_BASE = `${API_HOST}/api/shared/s3-image`;
const S3_DOMAIN = "xpressticket.s3.af-south-1.amazonaws.com";

/**
 * Fixes common issues with S3 image URLs
 * @param {string} imgSrc - The original image URL
 * @returns {string} - The fixed image URL or default image if null/undefined
 */
export function fixS3ImageUrl(imgSrc) {
  if (!imgSrc || imgSrc === '') return DEFAULT_IMAGE_DATA_URI;
  
  // Return as-is for local paths and data URIs
  if (imgSrc.startsWith('/') || imgSrc.startsWith('data:')) return imgSrc;
  
  let fixedUrl = imgSrc;
  
  try {
    // Handle double-encoded URLs
    if (fixedUrl.includes('%3A') || fixedUrl.includes('%2F')) {
      fixedUrl = decodeURIComponent(fixedUrl);
    }
    
    // Fix duplicate S3 domains: https://domain.com/https://domain.com/path
    if (fixedUrl.includes('xpressticket.s3') && fixedUrl.includes('//xpressticket.s3')) {
      const pathMatch = fixedUrl.match(/https?:\/\/[^\/]+\/(https?:\/\/[^\/]+\/(.+?)(\?.*)?$)/);
      if (pathMatch?.[2]) {
        fixedUrl = `https://${S3_DOMAIN}/${pathMatch[2]}${pathMatch[3] || ''}`;
      }
    }
    
    // Fix hostname issues
    const url = new URL(fixedUrl);
    if (url.hostname.includes('.s3.s3.')) {
      url.hostname = url.hostname.replace('.s3.s3.', '.s3.');
      fixedUrl = url.toString();
    } else if (url.hostname.includes('xpressticket') && url.hostname.includes('af-south-1.amazonaws.com') && !url.hostname.includes('.s3.')) {
      url.hostname = url.hostname.replace('.af-south-1.amazonaws.com', '.s3.af-south-1.amazonaws.com');
      fixedUrl = url.toString();
    }
    
    // Convert S3 URLs to proxy URLs
    if (fixedUrl.includes(S3_DOMAIN)) {
      const urlObj = new URL(fixedUrl);
      const key = urlObj.pathname.split('/').filter(Boolean).join('/');
      if (key) return `${S3_IMAGE_PROXY_BASE}/${encodeURIComponent(key)}`;
    }
    
    return fixedUrl;
  } catch (err) {
    // Fallback for invalid URLs - convert to absolute path if needed
    return fixedUrl.startsWith('/') ? fixedUrl : `/${fixedUrl}`;
  }
}

/**
 * Handles image loading with retry logic and fallback
 * @param {string} imageUrl - The URL of the image to load
 * @param {Function} onLoad - Callback when image loads successfully
 * @param {Function} onError - Callback when image fails to load
 */
export function handleImageLoading(imageUrl, onLoad, onError) {
  if (!imageUrl || imageUrl === DEFAULT_IMAGE_DATA_URI) {
    onLoad?.(DEFAULT_IMAGE_DATA_URI);
    return;
  }
  
  const img = new Image();
  
  img.onload = () => onLoad?.(imageUrl);
  
  img.onerror = () => {
    const fixedUrl = fixS3ImageUrl(imageUrl);
    if (fixedUrl !== imageUrl) {
      const retryImg = new Image();
      retryImg.onload = () => onLoad?.(fixedUrl);
      retryImg.onerror = () => onError?.(DEFAULT_IMAGE_DATA_URI);
      retryImg.src = fixedUrl;
    } else {
      onError?.(DEFAULT_IMAGE_DATA_URI);
    }
  };
  
  img.src = imageUrl;
}


/**
 * Converts a file to a data URL
 * @param {File} file - The file to convert
 * @returns {Promise<string>} A promise that resolves with the data URL
 */
export const fileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Checks if an image URL is valid
 * @param {string} url - The URL to check
 * @returns {Promise<boolean>} A promise that resolves with a boolean indicating if the image is valid
 */
export const isValidImageUrl = (url) => {
  if (!url) return Promise.resolve(false);
  
  return new Promise((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => {
      img.src = '';
      resolve(false);
    }, 5000);
    
    img.onload = img.onerror = () => {
      clearTimeout(timeout);
      resolve(img.complete && img.naturalHeight !== 0);
    };
    
    img.src = url;
  });
};

/**
 * Creates a thumbnail from an image URL
 * @param {string} url - The source image URL
 * @param {number} width - The desired width of the thumbnail
 * @param {number} height - The desired height of the thumbnail
 * @returns {Promise<string>} A promise that resolves with the thumbnail data URL
 */
export const createThumbnail = (url, width = 100, height = 100) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;
        
        // Calculate dimensions maintaining aspect ratio
        const aspectRatio = img.width / img.height;
        let newWidth = width, newHeight = height;
        
        if (aspectRatio > 1) {
          newHeight = width / aspectRatio;
        } else {
          newWidth = height * aspectRatio;
        }
        
        // Center and draw image
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, (width - newWidth) / 2, (height - newHeight) / 2, newWidth, newHeight);
        
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image for thumbnail'));
    img.src = url;
  });
};

